import React from 'react';
import objectPath from 'object-path';
import { observer, inject } from 'mobx-react';
import Alert from 'react-s-alert';
import styled from 'styled-components';
import { withNamespaces, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link } from 'core/routing';

import { Center } from 'legacyComponents/styled/lib';
import { Button, Icon, Heading } from 'components';
import { media } from 'legacyComponents/styled/styleUtils';
import { conf } from 'legacyComponents/cards/config.yaml';

import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';
import { PostForm } from 'apps/aggregator/postForm/index';
import { env } from 'process';

@withNamespaces()
@inject('routerStore')
@observer
export class NoDataCard extends React.Component {
  writePost = () => {
    modalStore.SetModalComponent(PostForm);
  };

  openIssue = () => {
    this.props.manager.OpenIssueForm();
  };

  createReadme = () => {
    this.props.manager.CreateReadme();
  };

  openReadmeTemplates = () => {
    this.props.manager.OpenReadmeTemplates();
  };

  thanksMessage = () => {
    Alert.success('You think? Thanks, that means a lot!');
  };

  welcomeMessage = () => {
    Alert.success("You're welcome.");
  };

  addIndustries = () => {
    this.props.manager.SetEditingIndustries(true);
  };

  addCertificates = () => {
    this.props.manager.SetEditingCertificates(true);
  };

  addServices = () => {
    const { routerStore } = this.props;
    const { route } = routerStore;
    routerStore.navigate('initiative.services.editFabrication', route.params);
  };

  render() {
    const {
      type,
      owner,
      ownerName,
      isChannel, // TODO Remove isChannel, isCollection, isWhitelabel when no parent component is using them
      isCollection,
      isWhitelabel,
      spaceType,
      t,
      routerStore,
    } = this.props;
    let lookUp;
    switch (spaceType) {
      case 'channel':
        lookUp = 'channelSpace';
        break;
      case 'collection':
        lookUp = 'collectionSpace';
        break;
      case 'whitelabel':
        lookUp = 'whitelabelSpace';
        break;
      case 'discover':
        lookUp = 'discoverSpace';
        break;
      default:
        /* TODO This block is here to ensure backwards compatibility.
            Once we refactor every noData usage to use spaceType instead of
            isChannel, isCollection or isWhitelabel we can leave only the
            owner prop check */
        lookUp = isChannel
          ? 'channelSpace'
          : isCollection
          ? 'collectionSpace'
          : isWhitelabel
          ? 'whitelabelSpace'
          : owner
          ? 'spaceOwners'
          : 'spaceViewers';
        break;
    }

    const buttons = [];
    const noDataDefault = {
      title: 'No data to show.',
      text: "There's no data available for this section",
    };
    const typeConf = objectPath.get(
      conf,
      [lookUp, type].join('.'),
      noDataDefault,
    );
    if (typeConf.buttons) {
      const btnProps = Object.keys(typeConf.buttons);

      btnProps.forEach(btn => {
        const button = typeConf.buttons[btn];
        let tailoredButton;

        // Create the button element, depending on the onClick existance
        if (button.onClick) {
          tailoredButton = (
            <ButtonStyled
              text={button.title}
              size='default'
              icon={button.icon && button.icon}
              iconPosition='right'
              onClick={this[button.onClick]}
              key={button.title}
            />
          );
        } else {
          tailoredButton = (
            <Link name={button.link} key={button.title}>
              <ButtonStyled
                size='default'
                icon={button.icon && button.icon}
                iconPosition='right'
                text={button.title}
                data-test-id={button.dataTestId}
              />
            </Link>
          );
        }

        /* Here, we "clear" that button for a specific form type, and a specific
           title. 
           TODO: Remove this condition and restore back the old method
           once the templates have been fully deployed
        */

        if (env.MODE === 'production') {
          if (type === 'readme' && button.title === 'Select template') {
            tailoredButton = undefined;
          }
        } else if (routerStore.path.split('/')[1] !== '+opennext') {
          if (type === 'readme' && button.title === 'Select template') {
            tailoredButton = undefined;
          }
        }

        // Only show the button if it passes the filter
        if (tailoredButton) {
          buttons.push(tailoredButton);
        }
      });
    }
    const appendName = [
      'project',
      'collection',
      'story',
      'industries',
      'certificates',
      'fabServices',
    ];
    const thinnerView = ['post', 'project', 'collection', 'story'];
    let cardText = t(typeConf.text);
    // FIXME
    // eslint-disable-next-line
    if ((!owner && appendName.includes(type))) {
      cardText = t(`{{ownerName}} ${typeConf.text}`, { ownerName });
    }
    const iconName = conf.icons[type];
    return (
      <Card small={thinnerView.includes(type) && !owner}>
        {thinnerView.includes(type) && (isChannel || !owner) ? (
          ''
        ) : (
          <>
            <NoDataIcon>
              {iconName && <Icon name={conf.icons[type]} size='xlarge' />}
            </NoDataIcon>
            <HeadingStyled level={4} size='medium'>
              <Trans>{typeConf.title}</Trans>
            </HeadingStyled>
          </>
        )}
        <TextWrapper>
          <Text margin={!thinnerView.includes(type) || owner}>{cardText}</Text>
        </TextWrapper>
        {['s', 'xs'].includes(clientStore.activeBreakPoint) ? (
          ''
        ) : (
          <Center>{buttons}</Center>
        )}
      </Card>
    );
  }
}

const Card = styled.div`
  height: auto;
  margin: 0 auto ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.color.wfGrey100};
  padding: ${props => (props.small ? '40px' : '80px 40px')};
`;

const NoDataIcon = styled.div`
  color: ${props => props.theme.color.wfBlack};
  border-radius: 50%;
  transition: left 250ms ease-in-out;
  margin: 0 auto;
  text-align: center;
`;

const HeadingStyled = styled(Heading)`
  text-align: center;
  margin: ${props => props.theme.spacing.default};
`;

const ButtonStyled = styled(Button)`
  margin: 0 ${props => props.theme.spacing.small};
`;

const TextWrapper = styled.div`
  width: 620px;
  display: block;
  margin: 0 auto;
  ${media.s`
    width: 95%;
  `};
`;

const Text = styled.p`
  text-align: center;
  line-height: 1.6;
  margin: ${props => (props.margin ? '16px 0 36px 0' : '0px')};
  ${media.s`
    width: 100%;
    margin: ${props => (props.margin ? '12px 0 20px 0' : '0px')};
  `};
`;

NoDataCard.propTypes = {
  type: PropTypes.string,
  owner: PropTypes.bool,
  ownerName: PropTypes.string,
  manager: PropTypes.object,
  isChannel: PropTypes.bool,
  isCollection: PropTypes.bool,
  isWhitelabel: PropTypes.bool,
  routerStore: PropTypes.object,
  spaceType: PropTypes.string,
  t: PropTypes.func,
};
